<template>
  <div v-if="property && property.answer" class="question-wrapper">
    <!-- Question Header -->
    <div class="question-header">
      <div class="question-content-wrapper">
        <div class="question-text">
          <ul v-if="property.question.includes('\n')" class="question-list">
            <li
              v-for="(line, index) in property.question.split('\n').filter(line => line.trim())"
              :key="'line-' + index"
            >
              {{ line }}
            </li>
          </ul>
          <span v-else>{{ property.question }}</span>
        </div>
      </div>

      <!-- Info Link (if URL hint) -->
      <a
        v-if="property.hint && (property.hint.startsWith('http') || property.hint.startsWith('https'))"
        :href="property.hint"
        target="_blank"
        rel="noopener noreferrer"
        class="info-link"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        References
      </a>

      <!-- Back Button -->
      <!-- <button
        class="btn-back"
        @click="back"
        :disabled="questionNo == 0"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15 19l-7-7 7-7"/>
        </svg>
        <span class="btn-text">Back</span>
      </button> -->
    </div>

    <!-- Separator -->
    <div class="separator"></div>

    <!-- Answer Component -->
    <div class="answer-section">
      <component
        v-bind:is="answerType"
        :answer="property.answer"
        :selectedAnswer="property.selectedAnswer"
        :questionId="property.id"
        :isReadOnly="isReadOnly"
        :isUser="true"
        :answers.sync="answers"
        :treatmentHistory.sync="treatmentHistory"
        :excludedRecommendations.sync="excludedRecommendations"
        :branchId.sync="branchId"
        :property.sync="property"
        :localGroups.sync="localGroups"
        :localQuestions.sync="localQuestions"
      />
    </div>

    <!-- Hint Section (if not URL) -->
    <div v-if="property.hint && !property.hint.startsWith('http') && !property.hint.startsWith('https')" class="hint-section">
      <div class="hint-card">
        <div class="hint-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
          </svg>
        </div>
        <p class="hint-text">{{ property.hint }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { Property } from "@/entities/questionaire/Property";
import AppBooleanAnswer from "@/components/questionnaire/answer-types/AppBooleanAnswer.vue";
import AppIntegerAnswer from "@/components/questionnaire/answer-types/AppIntegerAnswer.vue";
import AppSingleItemAnswer from "@/components/questionnaire/answer-types/AppSingleItemAnswer.vue";
import AppMultipleItemsAnswer from "@/components/questionnaire/answer-types/AppMultipleItemsAnswer.vue";
import { Answer } from "@/entities/questionaire/answers/Answer";
import { Treatment } from "@/entities/Treatment";
import { Group } from "@/entities/questionaire/Group";

@Component({
  components: {
    AppBooleanAnswer,
    AppIntegerAnswer,
    AppSingleItemAnswer,
    AppMultipleItemsAnswer,
  },
})
export default class AppQuestion extends Vue {
  answerType: string = "";

  @Prop()
  property!: Property;

  @Prop()
  isReadOnly!: boolean;

  @Prop()
  answers!: Answer[];

  @Prop()
  treatmentHistory!: Treatment[] | null;

  @Prop()
  branchId!: number | null;
  
  @Prop()
  questionNo!: number | 1;

  @Prop()
  excludedRecommendations!: {
    id: number;
    name: string;
    approvalPercentage: number;
  }[] | [];

  @Prop()
  public localGroups!: Group[];

  @Prop()
  localQuestions!: Property[];
  
  created(): void {
    if (this.property && this.property.answer) {
      this.answerType = this.property.answer.getComponentName();
    }
   
    if (this.property && !this.$store.state.localQuestionsTreatment.initQuestion) {
      this.localQuestions.forEach((question: Property) => {
        const questionObj = {
          question: question.question,
          id: question.id,
          treatment: 0,
          keyDecision: question.keyDecision,
        };
        this.$store.dispatch('localQuestionsTreatment/initializeLocalQuestions', questionObj);
      });
      
      this.$store.commit('localQuestionsTreatment/setInitQuestion', true);
    }
  }
 
  back(): void {
    this.$events.emit("onSetPreviousQuestion");
  }

   isValidUrl = (string: string): boolean => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.question-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.question-link:hover {
  color: #5b6fd8;
  text-decoration: underline;
}
.question-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: white;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 1.5rem;
}

/* Question Header */
.question-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.question-content-wrapper {
  flex: 1;
  min-width: 0;
}

.info-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.1), rgba(47, 69, 165, 0.05));
  color: #2f45a5;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  margin-bottom: 0;
  flex-shrink: 0;
  white-space: nowrap;
  transition: all 0.3s ease;
  border: 1px solid rgba(102, 123, 242, 0.2);

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(102, 123, 242, 0.15), rgba(47, 69, 165, 0.1));
    border-color: rgba(102, 123, 242, 0.3);
    transform: translateY(-1px);
  }
}

.question-text-container {
  display: none;
}

.question-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.1), rgba(47, 69, 165, 0.05));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    color: #667bf2;
  }
}

.question-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.6;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #94a3b8;
  }
}

/* Back Button */
.btn-back {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: rgba(102, 123, 242, 0.1);
  color: #2f45a5;
  border: 2px solid rgba(102, 123, 242, 0.2);
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover:not(:disabled) {
    background: rgba(102, 123, 242, 0.15);
    border-color: rgba(102, 123, 242, 0.3);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Separator */
.separator {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  //margin: 1.5rem 0;
}

/* Answer Section */
.answer-section {
  margin: 1.5rem 0;
}

/* Hint Section */
.hint-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.hint-card {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.05), rgba(47, 69, 165, 0.02));
  border-radius: 12px;
  border: 1px solid rgba(102, 123, 242, 0.1);
}

.hint-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667bf2, #2f45a5);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    color: white;
  }
}

.hint-text {
  color: #475569;
  line-height: 1.6;
  font-size: 0.95rem;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .question-wrapper {
    padding: 1.5rem;
  }

  .answer-section {
    margin: 0.75rem 0 0.5rem;
  }

  .question-header {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .question-text {
    font-size: 0.8rem;
  }

  .info-link {
    font-size: 0.7rem;
    padding: 0.3rem 0.625rem;
    gap: 0.3rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  .hint-card {
    flex-direction: column;
    text-align: center;
  }

  .hint-icon {
    margin: 0 auto;
  }
}

@media (max-width: 480px) {
  .question-wrapper {
    padding: 1rem;
    border-radius: 12px;
  }

  .question-text-container {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .question-icon {
    width: 28px;
    height: 28px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .question-text {
    font-size: 0.78rem;
  }

  .btn-back {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;

    .btn-text {
      display: none;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .info-link {
    width: 100%;
    justify-content: center;
    padding: 0.4rem 0.875rem;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .hint-card {
    padding: 0.875rem;
  }

  .hint-icon {
    width: 28px;
    height: 28px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .hint-text {
    font-size: 0.875rem;
  }
}
</style>